import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, inject, NgZone, OnDestroy, OnInit, QueryList, Signal, signal, ViewChild, ViewChildren } from '@angular/core';
import { OthersSchemaListComponent } from './others-schema-list/others-schema-list.component';
import { ActiveSchemaListComponent } from './active-schema-list/active-schema-list.component';
import { HistoricalSchemaListComponent } from './historical-schema-list/historical-schema-list.component';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { SchemaRepository } from '../../shared/repositories/schema.repository';
import { toSignal } from '@angular/core/rxjs-interop';
import { Schema } from '../../shared/models/schema.model';
import * as bootstrap from 'bootstrap';
import { OthersHistoricalSchemaListComponent } from './others-historical-schema-list/others-historical-schema-list.component';

@Component({
    selector: 'patient-schemas',
    templateUrl: './patient-schemas.component.html',
    imports: [
        CommonModule,
        OthersSchemaListComponent,
        OthersHistoricalSchemaListComponent,
        ActiveSchemaListComponent,
        HistoricalSchemaListComponent,
        BreadcrumbComponent,
        TranslocoDirective
    ]
})
export class PatientSchemasComponent implements AfterViewInit {
    private readonly schemaStore = inject(SchemaRepository);

    readonly activeSchemas: Signal<Schema[]> = toSignal(this.schemaStore.patientActiveSchemas$, { initialValue: [] });
    readonly historicalSchemas: Signal<Schema[]> = toSignal(this.schemaStore.patientHistoricalSchemas$, { initialValue: [] });
    readonly otherSchemas: Signal<Schema[]> = toSignal(this.schemaStore.patientOtherSchemas$, { initialValue: [] });
    readonly otherHistoricalSchemas: Signal<Schema[]> = toSignal(this.schemaStore.patientOtherCompletedSchemas$, { initialValue: [] });

    readonly hasActiveSchemas: Signal<boolean> = computed(() => this.activeSchemas().length > 0);
    readonly hasHistoricalSchemas: Signal<boolean> = computed(() => this.historicalSchemas().length > 0);
    readonly hasOthersSchemaAccess: Signal<boolean> = computed(() => this.otherSchemas().length > 0);
    readonly hasOthersHistoricalSchemaAccess: Signal<boolean> = computed(() => this.otherHistoricalSchemas().length > 0);

    readonly activeSchemaCount: Signal<number> = computed(() => this.activeSchemas().length);
    readonly historicalSchemaCount: Signal<number> = computed(() => this.historicalSchemas().length);
    readonly othersSchemaCount: Signal<number> = computed(() => this.otherSchemas().length);
    readonly othersHistoricalSchemaCount: Signal<number> = computed(() => this.otherHistoricalSchemas().length);

    
    @ViewChildren('tabButton') tabButtons!: QueryList<ElementRef<HTMLElement>>;

  selectedTab: 'active' | 'historical' | 'others' | 'othershistorical' = 'active';

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    // Wait until the buttons are rendered
    this.tabButtons.changes.subscribe(() => this.restoreTab());

    // Restore tab initially
    this.restoreTab();
  }

  restoreTab() {
    const savedTab = (sessionStorage.getItem('selectedTab') as 'active' | 'historical' | 'others' | 'othershistorical') || 'active';
    this.selectedTab = savedTab;
    
    const buttonToActivate = this.tabButtons.find(btn => {
      const id = btn.nativeElement.id;
      return (savedTab === 'active' && id.includes('active')) ||
             (savedTab === 'historical' && id.includes('historical')) ||
             (savedTab === 'others' && id.includes('others')) ||
             (savedTab === 'othershistorical' && id.includes('othershistorical'));
    });

    if (buttonToActivate) {
      const tab = new bootstrap.Tab(buttonToActivate.nativeElement);
      tab.show();
    }
  }

  onTabShown(tab: 'active' | 'historical' | 'others' | 'othershistorical') {
    this.ngZone.run(() => {
      this.selectedTab = tab;
      sessionStorage.setItem('selectedTab', tab);
    });
  }
}
